import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaHome,
  FaUser,
  FaProjectDiagram,
  FaCogs,
  FaEnvelope,
  FaGraduationCap,
  FaBriefcase,
  FaBars,
  FaTimes,
  FaDownload,
} from "react-icons/fa";

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: "smooth" });
    setIsOpen(false);
    setActiveSection(sectionId);
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map((item) => item.id);
      const currentSection = sections.find((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { name: "Home", id: "hero", icon: <FaHome /> },
    { name: "About", id: "about", icon: <FaUser /> },
    { name: "Education", id: "education", icon: <FaGraduationCap /> },
    { name: "Experience", id: "experience", icon: <FaBriefcase /> },
    { name: "Skills", id: "skills", icon: <FaCogs /> },
    { name: "Projects", id: "projects", icon: <FaProjectDiagram /> },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: -20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24,
      },
    },
  };

  const logoVariants = {
    hidden: { rotate: -180, opacity: 0 },
    visible: {
      rotate: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 20,
      },
    },
  };

  const buttonVariants = {
    rest: { scale: 1 },
    hover: { scale: 1.05 },
    tap: { scale: 0.95 },
  };

  return (
    <nav className="fixed w-full z-50 bg-gray-900 shadow-lg">
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={logoVariants}
            className="text-xl font-bold text-white flex items-center"
          >
            <svg
              className="w-8 h-8 mr-2 text-teal-400"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2L2 7L12 12L22 7L12 2Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2 17L12 22L22 17"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2 12L12 17L22 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-teal-400 to-teal-600">
              S. M. Shakib Hossain
            </span>
          </motion.div>

          {/* Desktop Menu */}
          <motion.div
            className="hidden md:flex space-x-6 items-center"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {navItems.map((item) => (
              <motion.button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`relative text-gray-300 hover:text-white transition-colors duration-300 group flex items-center ${
                  activeSection === item.id ? "text-teal-400 font-semibold" : ""
                }`}
                variants={itemVariants}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="mr-2">{item.icon}</span>
                {item.name}
                <span
                  className={`absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-teal-400 to-teal-600 transform transition-transform duration-300 ease-out ${
                    activeSection === item.id
                      ? "scale-x-100"
                      : "scale-x-0 group-hover:scale-x-100"
                  }`}
                />
              </motion.button>
            ))}
            <motion.a
              href="/Resume of S. M. Shakib Hossain.pdf" // Corrected path to your CV
              download
              className="bg-gradient-to-r from-teal-500 to-teal-700 text-white px-6 py-2 rounded-full flex items-center justify-center space-x-2 overflow-hidden relative"
              variants={buttonVariants}
              initial="rest"
              whileHover="hover"
              whileTap="tap"
            >
              <motion.span
                initial={{ x: 0 }}
                whileHover={{ x: -20 }}
                transition={{ duration: 0.3 }}
              >
                Download CV
              </motion.span>
              <motion.span
                className="absolute right-6"
                initial={{ x: 20, opacity: 0 }}
                whileHover={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <FaDownload />
              </motion.span>
            </motion.a>
          </motion.div>

          {/* Mobile Menu Toggle */}
          <div className="md:hidden">
            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-300 hover:text-white focus:outline-none"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="md:hidden mt-4 bg-gray-800 rounded-lg"
            >
              {navItems.map((item, index) => (
                <motion.button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`block w-full text-center py-2 px-4 transition-colors duration-300 flex items-center justify-center ${
                    activeSection === item.id
                      ? "text-teal-400 font-semibold bg-gray-700"
                      : "text-gray-300 hover:bg-gray-700 hover:text-white"
                  }`}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="mr-2">{item.icon}</span>
                  {item.name}
                </motion.button>
              ))}
              <motion.a
                href="/Resume of S. M. Shakib Hossain.pdf" // Corrected path to your CV
                download
                className="block w-full text-center py-2 px-4 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors duration-300 flex items-center justify-center mt-2"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: navItems.length * 0.1 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaDownload className="mr-2" />
                Download CV
              </motion.a>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navbar;
